import { createContext, useState, useContext, useEffect, useRef, useCallback } from "react";
import { ToastContext } from "cai-fusion";
import { useUserProfile } from "./UserProfileContext";
import { useSymphonyApiService } from "../../../hooks/useSymphonyApi";
import { useChat } from "./ChatContext";
import { useSignalR } from "./SignalRContext";
import { useSettings } from "./SettingsContext";
import { createUserFeedback } from "../utilities/UploadFeedback";
import { useDataStore } from "./DataStoreContext";
import { processFile } from "../../../components/fusion/FileUpload/helpers";
import { debugLog } from "../../../configs/loggingConfig";
import { normalizeChatDataStoreInclusions } from "./resourceContextUtils";

const ResourceContext = createContext();

function ResourceProvider({ children }) {
    const apiServiceClient = useSymphonyApiService("maestro", "v3");
    const frameworkServiceClient = useSymphonyApiService("framework", "v3");

    const { userProfile } = useUserProfile();
    const { modelEncoding } = useSettings();

    const { chatId, fetchChatId } = useChat();

    const [files, setFiles] = useState([]);
    const [backUpFiles, setBackUpFiles] = useState([]); // file backup in case of error

    const { dataStores, allDataStores } = useDataStore();
    const [enabledDataStores, setEnabledDataStores] = useState([]);
    const [disabledDataStores, setDisabledDataStores] = useState([]);

    const [dataSources, setDataSources] = useState([]);

    const { addMappingMethod } = useSignalR();
    const [resourceTokenCount, setResourceTokenCount] = useState(0);

    const [uploadComplete, setUploadComplete] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    // This is for controlling the text in the upload progress bar pop-up.
    const [uploadingFilesCount, setUploadingFilesCount] = useState(0);
    const { createToast } = useContext(ToastContext);

    // Function to add messages - will be set by MessageContext
    const [addMessage, setAddMessage] = useState(() => () => {});

    // Variable to share the status of uploading to other components
    const statusShare = (status) => {
        setUploadComplete(status !== 'error');
        if (status === 'error') setFiles([...backUpFiles]);
    };

    const calculateResourceTokenCount = useCallback((resources) => {
        if (!resources || resources.length === 0) {
            setResourceTokenCount(0);
            return;
        }

        const total = resources.filter(r => (r?.resourceRole ?? 0) !== 2).reduce((acc, file) => {
            // Add null check for resourceEncodings
            if (!file.resourceEncodings || !Array.isArray(file.resourceEncodings)) {
                debugLog.log("[CALCULATE] File missing resourceEncodings:", file.resourceName);
                return acc;
            }
            
            const itemSum = file.resourceEncodings.reduce((tokenAcc, item) => {
                // Check if the condition is true and return the new token count
                if (item.encodingEngine === modelEncoding) return tokenAcc + item.tokenCount;
                // If the condition is not met, return the tokenAcc as it is
                return tokenAcc;
            }, 0);
            return acc + itemSum;
        }, 0);

        setResourceTokenCount(total);
    }, [modelEncoding]);

    const getChatResources = useCallback(async (chatId) => {
        if (!chatId) return;
        
        debugLog.log("[GETRESOURCE] Fetching resources for chatId:", chatId);
        try {
            const resources = await apiServiceClient.Chats.getChatResources(chatId);
            debugLog.log("[GETRESOURCE] Resources received:", resources);
            if (resources) {
                setFiles([...resources]);
                calculateResourceTokenCount(resources);
                debugLog.log("[GETRESOURCE] Files set to:", resources);
            } else {
                debugLog.log("[GETRESOURCE] No resources returned, setting empty array");
                setFiles([]);
            }
            
        } catch (error) {
            debugLog.log("[GETRESOURCE] Error " + error);
            setFiles([...backUpFiles]);
            calculateResourceTokenCount(backUpFiles);
        }
    }, [apiServiceClient.Chats, backUpFiles, calculateResourceTokenCount]);

    // Active memory adjustments during uploading sessions 
    const activeUploadTokenCount = useCallback(async (items) => {
        if (!items || items.length === 0) return;
        
        const sum = items.reduce((acc, item) => {
            if (item.encodingEngine === modelEncoding) return acc + item.tokenCount;
            return acc;
        }, 0);
        setResourceTokenCount(prevCount => prevCount + sum);
    }, [modelEncoding]);

    
    const getDataCollections = useCallback(async (activeChatId) => {
        if (!activeChatId) {
            setEnabledDataStores([]);
            return;
        }

        debugLog.log("[GETDATASTOREINCLUSIONS] Getting collections");
        try {
            const resp = await apiServiceClient.Chats.GetChatDataStores(activeChatId);

            // API compatibility: some responses return { message: [...] }, older code expected { message: { dataStores: [...] } }
            const inclusions = normalizeChatDataStoreInclusions(resp);

            const enabledDataCollections = inclusions.filter(dataStore => dataStore?.isEnabled);

            debugLog.log("[GETDATASTOREINCLUSIONS] Enabled Data Collections: ", enabledDataCollections);

            const enabledDataStorePromises = enabledDataCollections.map(async dataStore => {
                const additionalData = await apiServiceClient.DataStore.getDataStore(dataStore.dataStoreId);
                return additionalData;
            });

            const detailedEnabledDataStores = await Promise.all(enabledDataStorePromises);
            debugLog.log("[GETDATASTOREINCLUSIONS] Detailed Enabled Data Stores: ", detailedEnabledDataStores);
            setEnabledDataStores(detailedEnabledDataStores.filter(Boolean));
        } catch (error) {
            debugLog.log("[GETDATASTOREINCLUSIONS] Error: ", error);
            setEnabledDataStores([]);
        }
    }, [apiServiceClient.Chats, apiServiceClient.DataStore]);

    useEffect(() => {
        getDataCollections(chatId);
    }, [chatId, getDataCollections]);

    useEffect(() => {
        const getDataSources = async () => {
            debugLog.log("[GETDATASOURCES] Getting data sources...");
            // let respDataSources = await apiServiceClient.DataSources.getExternalDataSources();
            setDataSources([]);
        }
        getDataSources();
    }, [userProfile, apiServiceClient.DataSources]);

    useEffect(() => {
        debugLog.log(`Resource Token Count Upladed to: ${resourceTokenCount}`);
    }, [resourceTokenCount]);

    // For new chat and selecting a different chat.
    // Keep data store state sourced from /Chats/{id}/datacollection to avoid stale/empty
    // values from partial chat payloads during refresh.
    useEffect(() => {
        if (!isUploading) {
            if (!chatId) {
                setFiles([]);
                setResourceTokenCount(0);
                setEnabledDataStores([...allDataStores.filter(x => x.isFavorited)]);
            } else {
                getChatResources(chatId);
            }
        }
    }, [chatId, dataStores, isUploading, allDataStores, getChatResources]);

    // Function to add file upload notification messages to chat
    //const addResourceToMessages = useCallback((successfulFiles) => {
    //    if (!successfulFiles.length || !addMessage) return;

    //    const fileNames = successfulFiles.map(file => file.resourceName);
    //    const messageText = fileNames.join(", ");

    //    const infoMessage = {
    //        messageId: "info-data-upload",
    //        role: "info-data",
    //        content: [{ 
    //            type: "text", 
    //            contentOrder: 0, 
    //            text: messageText 
    //        }],
    //        messageDate: new Date().toISOString(),
    //        userProfileId: userProfile?.userProfileId,
    //        isLoading: false
    //    };

    //    addMessage(infoMessage);
    //}, [addMessage, userProfile?.userProfileId]);

    const handleFilesUpload = async (addedFiles) => {
        if (!Array.isArray(addedFiles)) addedFiles = Array.from(addedFiles); // may return as non Array types, Array.from fixes this
        addedFiles = await Promise.all(addedFiles.map(file => processFile(file)));

        setBackUpFiles([...files]);

        addedFiles = filterDuplicates(files, addedFiles);
        if (!addedFiles.length) return;

        debugLog.log("Welcome to ResourceContext");

        setUploadingFilesCount(addedFiles.length);
        setIsUploading(true);
        setFiles(prevFiles => [...prevFiles, ...addedFiles]);

        const sessionChatId = await fetchChatId(); // Make sessionChatId go up first

        await Promise.all(addedFiles.map(file => uploadChatResource(sessionChatId, file)));
        await getChatResources(sessionChatId);

        // Add notification messages for successfully uploaded files
        const successfulFiles = addedFiles.filter(file => file.isSuccess);
        if (successfulFiles.length > 0) {
            //addResourceToMessages(successfulFiles);
        }

        chatResourceUserFeedback(addedFiles);
        setIsUploading(false);
    };

    const filterDuplicates = (currentFiles, addedFiles) => {
        const nonDuplicateFiles = addedFiles.filter(addedFile =>
            !currentFiles.some(file => file.resourceName === addedFile.resourceName)
        );
        return nonDuplicateFiles;
    };

    // Does the API call to upload a new resource.
    const uploadChatResource = async (sessionChatId, file) => {
        try {
            const response = await apiServiceClient.Chats.addChatResource(sessionChatId, file);
            
            debugLog.log(`[UPLDRESOURCE] Upload resource response: ${JSON.stringify(response, null, 2)}`);
            
            file.isSuccess = response?.resourceId != null;
            file.Message = response?.message || (file.isSuccess ? "" : "An unknown error");
            
            if (file.isSuccess) {
                updateUploadingStatus(file);
            }

            // Do not update the count if it's a data role resource (role 2) or the property is missing
            if (response.result?.resourceRole !== 2) {
                // await activeUploadTokenCount(response.result.resourceEncodings);
            }
        } catch (error) {
            // A catch all error, controlled errors get handled in if condition above
            debugLog.error(`[UPLOAD] Error uploading file: ${file.resourceName}`, error);
            file.isSuccess = false;
            file.Message = "Internal Problem. Try uploading Resource Again.";
        }
        return file;
    };

    // File state changes during upload session
    const updateUploadingStatus = (file) => {
        setFiles(prevFiles => {
            const fileIndex = prevFiles.findIndex(fileMain => fileMain.resourceName === file.resourceName);
            if (fileIndex === -1) return prevFiles;

            const updatedFiles = [...prevFiles];
            updatedFiles[fileIndex] = { ...updatedFiles[fileIndex], uploading: false };
            return updatedFiles;
        });
    };

    // When a data source is included/no longer included
    const handleDataSourcesChange = (index) => {
        const newDataSource = [...dataSources];
        newDataSource[index].included = !newDataSource[index].included;
        setDataSources(newDataSource);
    };



    // Delete existing chat resource
    const deleteChatResource = async (fileToDelete) => {
        if (fileToDelete.resourceId === undefined) {
            debugLog.log("[DELRESOURCE] File resourceId is undefined, cannot delete");
            return;
        }
        
        try {
            const newFiles = files.filter(file => file.resourceId !== fileToDelete.resourceId);
            setFiles(newFiles);
            await apiServiceClient.Chats.deleteChatResource(chatId, fileToDelete.resourceId);
            calculateResourceTokenCount(newFiles);
        } catch (error) {
            debugLog.log("[DELRESOURCE] Error: " + error + " with ChatId: " + chatId + " and file.resourceId: " + fileToDelete.resourceId);
            // Revert UI changes on error
            setFiles([...files]);
            calculateResourceTokenCount(files);
        }
    };

    const chatResourceUserFeedback = (addedFiles) => createUserFeedback(addedFiles, statusShare, createToast);

    // SignalR response to new resource being added
    const receiveNewResource = useCallback(async (resources) => {
        const currentChatId = await fetchChatId();
        if (resources[0]?.chatId === currentChatId) {
            // Add resource to current chat
            debugLog.log(`[RECVRESOURCE] Adding resource to current chat: ${resources[0].resourceName}`);
            try {
                const fileIndex = filesRef.current.findIndex(x => x.resourceName === resources[0].resourceName);
                if (fileIndex === -1) {
                    setFiles([...filesRef.current, resources[0]]);
                    // Add message notification for received resource
                    //addResourceToMessages([resources[0]]);
                } else {
                    setFiles([...filesRef.current.slice(0, fileIndex), resources[0], ...filesRef.current.slice(fileIndex + 1)]);
                }
            } catch (error) {
                debugLog.error("[RECVRESOURCE] Error getting chat", error);
            }
        }
        // This is a place for code to be executed if the message was sent in a chat that is not the current chat.
    }, [fetchChatId]);

    const filesRef = useRef();

    useEffect(() => {
        filesRef.current = files
    }, [files])

    useEffect(() => {
        addMappingMethod({
            // This method will be called when a new resource is received from the user. 
            // This resource will have an empty GUID since it is not yet finalized in the database.
            TextName: "ReceiveNewResource",
            Method: (resource) => {
                const resourceJson = JSON.stringify(resource);
                debugLog.log(`[RESOURCE] ${resourceJson}`);
                receiveNewResource([resource]);
                // resource should contain: chatId, resourceName
            },
        });
    }, [addMappingMethod, receiveNewResource]);

    const enableDataStore = async (dataStore) => {
        await apiServiceClient.Chats.EnableDatastore(await fetchChatId(), dataStore.id);
        setEnabledDataStores([...enabledDataStores, dataStore]);
    };

    const disableDataStore = async (dataStore) => {
        await apiServiceClient.Chats.DisableDataStore(await fetchChatId(), dataStore.id);
        setEnabledDataStores(enabledDataStores.filter(x => x.id !== dataStore.id));
    };

    /**
     * Fetches a resource with authentication and returns a blob URL.
     * The caller is responsible for revoking the URL when done (URL.revokeObjectURL).
     *
     * @param {string} resourceId - The resource ID to fetch
     * @returns {Promise<string>} - Promise resolving to an object URL for the resource
     */
    const fetchResourceBlobUrl = useCallback(async (resourceId) => {
        try {
            const blob = await frameworkServiceClient.Resources.getResource(resourceId);
            const blobUrl = URL.createObjectURL(blob);
            return blobUrl;
        } catch (error) {
            debugLog.error('[FETCHRESOURCE] Error fetching resource:', error);
            throw error;
        }
    }, [apiServiceClient]);

    return (
        <ResourceContext.Provider
            value={{
                // States
                uploadComplete,
                setUploadComplete,
                resourceTokenCount,
                dataSources,
                setDataSources,
                files,
                setFiles,
                enabledDataStores,
                setEnabledDataStores,
                isUploading,
                uploadingFilesCount,
                // Functions
                handleDataSourcesChange,
                enableDataStore,
                disableDataStore,
                handleFilesUpload,
                uploadChatResource,
                deleteChatResource,
                fetchResourceBlobUrl, // For fetching authenticated resource blob URLs
                setAddMessage, // For MessageContext to register its addMessage function
            }}
        >
            {children}
        </ResourceContext.Provider>
    );
}

function useResource() {
    const context = useContext(ResourceContext);
    if (!context) throw new Error("useResource must be used within a ResourceProvider");
    return context;
}

export { ResourceProvider, useResource };